import React, { useState } from 'react';
import AdminShell from '../../../components/admin/AdminShell';
import { useTemplates, useDeleteTemplate } from '../../../hooks/useMarketingQueries';
import { useFilters } from '../../../hooks/useFilters';
import { useDebounce } from '../../../hooks/useDebounce';
import { exportTemplatesToCSV } from '../../../lib/csvExport';
import { toast } from 'sonner';
import { Plus, Search, Download, Trash2, Edit, Copy, AlertCircle, ArrowUpDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';
import { marketingApi } from '../../../lib/api';

export default function TemplatesPage() {
  const navigate = useNavigate();
  const { filters, setFilter } = useFilters({
    search: '',
    channel: 'all',
    sort: 'updated_desc',
  });
  
  const debouncedSearch = useDebounce(filters.search, 350);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  const deleteTemplate = useDeleteTemplate();
  
  // Query params for API
  const queryParams = {
    search: debouncedSearch || undefined,
    channel: filters.channel === 'all' ? undefined : filters.channel,
  };
  
  const { data: templates = [], isLoading, error } = useTemplates(queryParams);
  
  // Sort templates client-side
  const sortedTemplates = React.useMemo(() => {
    const sorted = [...templates];
    const [field, direction] = filters.sort.split('_');
    
    sorted.sort((a, b) => {
      let aVal, bVal;
      if (field === 'name') {
        aVal = a.name?.toLowerCase() || '';
        bVal = b.name?.toLowerCase() || '';
      } else if (field === 'channel') {
        aVal = a.channel || '';
        bVal = b.channel || '';
      } else if (field === 'updated') {
        aVal = new Date(a.updated_at);
        bVal = new Date(b.updated_at);
      }
      
      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return sorted;
  }, [templates, filters.sort]);
  
  const handleDelete = (id) => {
    deleteTemplate.mutate(id, {
      onSuccess: () => setDeleteId(null),
    });
  };
  
  const handleBulkDelete = () => {
    Promise.all(selectedIds.map(id => marketingApi.deleteTemplate(id)))
      .then(() => {
        toast.success(`${selectedIds.length} templates deleted`);
        setSelectedIds([]);
        setBulkDeleteDialogOpen(false);
        window.location.reload(); // Simple refresh to update list
      })
      .catch(() => toast.error('Failed to delete some templates'));
  };
  
  const handleDuplicate = (template) => {
    const duplicated = { ...template, name: `${template.name} (Copy)` };
    delete duplicated._id;
    marketingApi.createTemplate(duplicated)
      .then(() => {
        toast.success('Template duplicated');
        window.location.reload();
      })
      .catch(() => toast.error('Failed to duplicate template'));
  };
  
  const handleExport = () => {
    exportTemplatesToCSV(sortedTemplates, 'templates.csv');
    toast.success('Templates exported to CSV');
  };
  
  const toggleSelectAll = () => {
    if (selectedIds.length === sortedTemplates.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(sortedTemplates.map(t => t._id));
    }
  };
  
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };
  
  const channelBadge = (channel) => {
    const styles = {
      sms: 'bg-[rgba(42,166,161,0.12)] border-[rgba(42,166,161,0.22)] text-[#A0E7E5]',
      whatsapp: 'bg-[rgba(61,220,151,0.12)] border-[rgba(61,220,151,0.22)] text-[#BFF3D2]',
    };
    return (
      <Badge className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs border ${styles[channel] || styles.sms}`}>
        {channel?.toUpperCase()}
      </Badge>
    );
  };
  
  return (
    <AdminShell 
      title="Templates" 
      actions={
        <div className="flex items-center gap-2">
          {sortedTemplates.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40 gap-2"
              data-testid="templates-export-button"
              aria-label="Export templates to CSV"
            >
              <Download size={16} />
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
          )}
          <Button 
            onClick={() => navigate('/admin/marketing/templates/new')}
            className="bg-[#D4A843] text-black hover:brightness-110 gap-2"
            data-testid="templates-new-button"
            aria-label="Create new template"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">New Template</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B9B9B9]" size={16} aria-hidden="true" />
            <Input
              placeholder="Search templates..."
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
              className="pl-10 bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]"
              data-testid="templates-search-input"
              aria-label="Search templates"
            />
          </div>
          <Select value={filters.channel} onValueChange={(v) => setFilter('channel', v)}>
            <SelectTrigger 
              className="w-full sm:w-[180px] bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]" 
              data-testid="templates-channel-filter"
              aria-label="Filter by channel"
            >
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.sort} onValueChange={(v) => setFilter('sort', v)}>
            <SelectTrigger 
              className="w-full sm:w-[180px] bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]"
              aria-label="Sort templates"
            >
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updated_desc">Latest Updated</SelectItem>
              <SelectItem value="updated_asc">Oldest Updated</SelectItem>
              <SelectItem value="name_asc">Name (A-Z)</SelectItem>
              <SelectItem value="name_desc">Name (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Bulk Actions Toolbar */}
        {selectedIds.length > 0 && (
          <div className="bg-[rgba(212,168,67,0.08)] border border-[rgba(212,168,67,0.22)] rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm text-[#F2F2F2]">
              {selectedIds.length} template{selectedIds.length > 1 ? 's' : ''} selected
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkDeleteDialogOpen(true)}
              className="bg-transparent border-[#2A2A2A] text-[#FF4D4D] hover:bg-[rgba(255,77,77,0.1)] gap-2"
              data-testid="bulk-delete-button"
              aria-label="Delete selected templates"
            >
              <Trash2 size={14} />
              Delete Selected
            </Button>
          </div>
        )}

        {/* Table */}
        <div className="surface-card card-highlight rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-[#2A2A2A]">
                <tr>
                  <th className="w-12 px-5 py-3">
                    <Checkbox
                      checked={sortedTemplates.length > 0 && selectedIds.length === sortedTemplates.length}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all templates"
                    />
                  </th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">
                    Name
                  </th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">
                    Channel
                  </th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">
                    Variables
                  </th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">
                    Updated
                  </th>
                  <th className="text-right px-5 py-3 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E1E1E]">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-5 py-3">
                        <div className="h-4 w-4 bg-[#202020] rounded animate-pulse" />
                      </td>
                      {Array.from({ length: 5 }).map((_, j) => (
                        <td key={j} className="px-5 py-3">
                          <div className="h-4 bg-[#202020] rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : error ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <AlertCircle size={40} className="mx-auto mb-3 text-[#FF4D4D]" />
                      <p className="text-[#B9B9B9] text-sm">Failed to load templates</p>
                    </td>
                  </tr>
                ) : sortedTemplates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <AlertCircle size={40} className="mx-auto mb-3 text-[#6F6F6F]" />
                      <p className="text-[#B9B9B9] text-sm mb-2">No templates found</p>
                      <p className="text-[#6F6F6F] text-xs mb-4">Use variables like {'{customer_name}'} in your templates</p>
                      <Button
                        onClick={() => navigate('/admin/marketing/templates/new')}
                        className="bg-[#D4A843] text-black hover:brightness-110"
                      >Create your first template</Button>
                    </td>
                  </tr>
                ) : (
                  sortedTemplates.map(template => (
                    <tr 
                      key={template._id} 
                      className={`hover:bg-[#161616] transition-colors ${selectedIds.includes(template._id) ? 'bg-[rgba(212,168,67,0.05)]' : ''}`}
                      data-testid="template-row"
                    >
                      <td className="px-5 py-3">
                        <Checkbox
                          checked={selectedIds.includes(template._id)}
                          onCheckedChange={() => toggleSelect(template._id)}
                          aria-label={`Select ${template.name}`}
                        />
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-sm text-[#F2F2F2] font-medium">{template.name}</p>
                        <p className="text-xs text-[#6F6F6F] truncate max-w-xs">{template.template_body?.substring(0, 50)}{template.template_body?.length > 50 ? '...' : ''}</p>
                      </td>
                      <td className="px-5 py-3">
                        {channelBadge(template.channel)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {template.variables_detected?.slice(0, 3).map(v => (
                            <span key={v} className="inline-block px-1.5 py-0.5 rounded bg-[rgba(212,168,67,0.14)] border border-[rgba(212,168,67,0.28)] text-[10px] font-mono text-[#D4A843]">
                              {v}
                            </span>
                          ))}
                          {template.variables_detected?.length > 3 && (
                            <span className="text-xs text-[#6F6F6F]">+{template.variables_detected.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-xs text-[#B9B9B9]">{new Date(template.updated_at).toLocaleDateString()}</p>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/marketing/templates/${template._id}/edit`)}
                            className="text-[#B9B9B9] hover:text-[#F2F2F2]"
                            data-testid="template-edit-button"
                            aria-label="Edit template"
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicate(template)}
                            className="text-[#B9B9B9] hover:text-[#F2F2F2]"
                            data-testid="template-duplicate-button"
                            aria-label="Duplicate template"
                          >
                            <Copy size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(template._id)}
                            className="text-[#B9B9B9] hover:text-[#FF4D4D]"
                            data-testid="template-delete-button"
                            aria-label="Delete template"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#111111] border-[#2A2A2A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F2F2F2]">Delete Template</AlertDialogTitle>
            <AlertDialogDescription className="text-[#B9B9B9]">
              Are you sure? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)} className="bg-[#FF4D4D] text-white hover:brightness-110">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#111111] border-[#2A2A2A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F2F2F2]">Delete {selectedIds.length} Templates</AlertDialogTitle>
            <AlertDialogDescription className="text-[#B9B9B9]">
              Are you sure you want to delete {selectedIds.length} template{selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-[#FF4D4D] text-white hover:brightness-110">Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
